import { I18n } from "@assets/strings";
import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useHistory } from "@core/routing/useHistory";
import { Redirect } from "@core/routing/redirect";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { Path, ShareroomTab, shareroomTabs } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useIsLearner } from "@domain/currentProfile/hooks";
import { Group } from "@domain/group/group";
import { useGroup } from "@domain/group/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { ShareroomGuideModal } from "@ui/components/group/shareroom/shareroomGuide/shareroomGuideModal";
import { ArrowIcon } from "@ui/components/icons";
import { stack } from "@ui/components/layout";
import { ScrollBehavior } from "@ui/components/navigation/scrollToTop";
import { NoMaxWidthPage, Page } from "@ui/components/page";
import { TabSelector } from "@ui/components/tabSelector";
import { useModalTools } from "@ui/modals/modalContext";
import { theme } from "@ui/styles/theme";
import { mobile } from "@ui/styles/utils";
import { LivecoursesCarouselGroup } from "@ui/pages/livecourses/livecoursesCarousel";
import { NotFoundPage } from "@ui/pages/notFoundPage";
import { GroupActivity } from "./groupActivity";
import { GroupCommunity } from "./groupCommunity";
import { GroupAccess } from "./groupeAccess";
import { GroupPresentation } from "./groupPresentation";
import { DashboardPage } from "@ui/pages/group/shareroom/dashboard/dashboardPage";
import { ShareroomFeedback } from "@ui/pages/group/shareroom/feedback/shareroomFeedback";
import { ShareroomPdf } from "@ui/pages/group/shareroom/pdf/shareroomPdf";
import { ShareroomHeader } from "./shareroomHeader";
import { ShareroomWorkspaces } from "./shareroomWorkspaces";
import { SubscriptionError } from "./subscriptionError";
import { SubscriptionSuccess } from "./subscriptionSuccess";
import { WorkspacesCarousel } from "@ui/pages/group/workspace/workspacesCarousel";

interface ShareroomPageProps extends React.HTMLAttributes<HTMLDivElement> {
	shareroomId?: string;
	shareroomSlug?: string;
	tab?: ShareroomTab;
	workspaceId?: string;
	isNewShareroom?: "NEW";
	action?: "tups" | "tupf";
}

export const ShareroomPage: React.FC<ShareroomPageProps> = ({
	shareroomId,
	workspaceId,
	shareroomSlug,
	isNewShareroom,
	tab = "about",
	action,
	...htmlProps
}) => {
	const { result: shareroom, loading, error } = useGroup(shareroomId ?? "", shareroomSlug);
	const { groupService } = useServices();

	const currentProfile = useCurrentProfile();
	const isLearner = useIsLearner();
	const history = useHistory();

	const location = useLocation();

	const { createModal } = useModalTools();

	const openGuideModal = (shareroom: Group): void => {
		createModal((close) => <ShareroomGuideModal shareroom={shareroom} close={close} />, { closeOnBackdropClick: true });
	};

	useEffect(() => {
		if (location.state && location.state === "NEW" && shareroom) {
			if (Environment.APP_VARIANT !== "jaangal") {
				openGuideModal(shareroom);
			}
		}
	}, [location.state]);

	useEffect(() => {
		if (action === "tups") {
			createModal((close) => <SubscriptionSuccess close={close} />);
		}
		if (action === "tupf") {
			createModal((close) => <SubscriptionError close={close} />);
		}
	}, []);

	useEffect(() => {
		groupService.setAlias(shareroom);
	}, [shareroom]);

	const displayedTabs = useMemo(() => {
		let tabs: ShareroomTab[];
		if (shareroom?.userRole === "ADMINISTRATOR") {
			if (shareroom?.owner.id === currentProfile?.id) {
				tabs = shareroomTabs;
			} else {
				tabs = shareroomTabs.filter((tab) => tab !== "dashboard");
			}
		} else if (!shareroom?.userRole || shareroom?.userRole === "MEMBER") {
			tabs = shareroomTabs.filter((tab) => tab !== "dashboard");
		} else {
			const hideCommunity = !shareroom?.memberCounts.MEMBER;
			tabs = shareroomTabs.filter((tab) => tab !== "community" || !hideCommunity);
		}

		if (isLearner) {
			return tabs.filter((tab) => tab !== "dashboard");
		}
		return tabs;
	}, [shareroom, currentProfile, isLearner]);

	if (error?.code === 404) {
		return <NotFoundPage message={I18n.t("404.group")} />;
	}

	// this rederiction as necessary because technicaly an shareroom is a group
	if (shareroom?.type === "DISCUSSION_GROUP") {
		return <Redirect to={Path.Discussion({ id: shareroomId, slug: shareroomSlug })} />;
	}
	if (tab === "dashboard" && (isLearner || shareroom?.owner.id !== currentProfile?.id)) {
		return <Redirect to={Path.Shareroom({ id: shareroomId, slug: shareroomSlug })} />;
	}

	return !shareroom ? (
		loading ? (
			<ClipLoader />
		) : null
	) : workspaceId &&
	  !(!shareroom.userRole || shareroom.status === "PENDING_HOST_ACCEPTANCE") &&
	  !(shareroom.status === "PENDING_GUEST_ACCEPTANCE") ? (
		<NoMaxWidthContainer>
			<ShareroomWorkspaces shareroom={shareroom} workspaceId={workspaceId} slugWorkspace="" />
		</NoMaxWidthContainer>
	) : (
		<Container {...htmlProps}>
			{/* <HelmetInfos
				title={I18n.t(`seo.group.${shareroom.type}.title`, { name: shareroom.name })}
				description={[shareroom.description, shareroom.categories.map((cat) => I18n.t(`category.${cat}`)).join(", ")]
					.filter(Boolean)
					.join(" - ")}
			/> */}
			{!workspaceId && shareroom.organization ? (
				<StyledBackButton>
					<StyledArrowIcon onClick={() => history.push(Path.Organization({ ...shareroom.organization }))}>
						<ArrowIcon color={theme.colors.secondary} />
					</StyledArrowIcon>
					<BackTitle>{I18n.t("group.goback.organisation")}</BackTitle>
				</StyledBackButton>
			) : null}
			{!workspaceId ? <ShareroomHeader shareroom={shareroom} /> : null}

			{shareroom.license === "FREE" &&
			(!shareroom.userRole ||
				shareroom.status === "PENDING_GUEST_ACCEPTANCE" ||
				shareroom.status === "PENDING_HOST_ACCEPTANCE") ? (
				<GroupAccess group={shareroom} />
			) : (
				<React.Fragment>
					<StyledTabSelector
						items={displayedTabs}
						selectedIndex={displayedTabs.indexOf(tab)}
						onItemSelected={(index, tab) => {
							if (tab === "feedback" || tab === "about") {
								history.push(Path.Shareroom(shareroom, tab), ScrollBehavior.NoScroll);
								return;
							}
							if (
								shareroom.license === "PREMIUM" &&
								(!shareroom.subscriptionStatus || shareroom.subscriptionStatus === "EXPIRED") &&
								shareroom.owner.id !== currentProfile?.id &&
								shareroom.administrators.findIndex((admin) => admin.id === currentProfile?.id) < 0
							) {
								return;
							}
							history.push(
								Path.Shareroom(shareroom, index === 0 ? undefined : displayedTabs[index]),
								ScrollBehavior.NoScroll
							);
						}}
						itemRenderer={(item) => I18n.t(`group.tab.${item}`)}
					/>
					{tab === "workspaces" ? (
						<WorkspacesCarousel shareroom={shareroom} />
					) : tab === "community" ? (
						<GroupCommunity group={shareroom} />
					) : tab === "activity" ? (
						<GroupActivity group={shareroom} />
					) : tab === "about" ? (
						<GroupPresentation group={shareroom} />
					) : tab === "live" ? (
						<LivecoursesCarouselGroup shareroom={shareroom} />
					) : tab === "dashboard" ? (
						<DashboardPage currentProfile={currentProfile} shareroom={shareroom} />
					) : tab === "feedback" ? (
						<ShareroomFeedback shareroom={shareroom} />
					) : tab === "pdf" ? (
						<ShareroomPdf shareroom={shareroom} />
					) : null}
				</React.Fragment>
			)}

			{!currentProfile && shareroom && (
				<AppAuthIncentive message={I18n.t(`group.${shareroom?.type}.auth.incentive`, { name: shareroom?.name })} />
			)}
		</Container>
	);
};

const Container = styled(Page)`
	${stack(24, "flex-start", "stretch")};
	@media ${mobile} {
		${stack(30, "flex-start", "stretch")};
	}
`;

const StyledTabSelector = styled(TabSelector)`
	${({ theme }) => theme.containerStyles.roundedBlockStyle};
	padding: 15px 40px;
	/* margin-bottom: 50px !important; */
`;

const NoMaxWidthContainer = styled(NoMaxWidthPage)`
	${stack(45, "flex-start", "stretch")};
	@media ${mobile} {
		${stack(30, "flex-start", "stretch")};
	}
`;

const StyledBackButton = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`;
const StyledArrowIcon = styled.div`
	width: 50px;
	height: 50px;
	border-radius: 50%;
	cursor: pointer;
	background-color: ${({ theme }) => theme.colors.white};
	display: flex;
	align-items: center;
	justify-content: center;
`;

const BackTitle = styled.div`
	${({ theme }) => theme.textStyles.title}
`;
